// DAS-2107: "Your Momentum" modal for PortfolioAnalysisView
// Section 4 (Summary bar) + Section 1 (Multi-timeframe heatmap) + Section 2 (Trend & Technical) + Section 3 (Quantitative)

import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Chip, Stack, Tooltip, Tabs, Tab, IconButton,
  Table as MuiTable, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Paper, TableSortLabel,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ClickableTickerChip from './ClickableTickerChip';

const pf = (v, d = 1) => {
  const n = parseFloat(v);
  if (v == null || isNaN(n)) return null;
  return n;
};

const fmt = (v, d = 1) => {
  if (v == null) return '—';
  return v.toFixed(d);
};

const signed = (v, d = 1) => {
  if (v == null) return '—';
  return `${v >= 0 ? '+' : ''}${v.toFixed(d)}%`;
};

const perfColor = (v) => {
  if (v == null) return undefined;
  if (v >= 5) return '#1b5e20';
  if (v > 0) return '#4caf50';
  if (v === 0) return '#757575';
  if (v > -5) return '#e53935';
  return '#b71c1c';
};

const perfBgColor = (v) => {
  if (v == null) return 'transparent';
  if (v >= 10) return 'rgba(27,94,32,0.12)';
  if (v >= 5) return 'rgba(76,175,80,0.10)';
  if (v > 0) return 'rgba(76,175,80,0.05)';
  if (v === 0) return 'transparent';
  if (v > -5) return 'rgba(229,57,53,0.05)';
  if (v > -10) return 'rgba(229,57,53,0.10)';
  return 'rgba(183,28,28,0.12)';
};

const rsiZone = (v) => {
  if (v == null) return { label: '—', color: 'default' };
  if (v > 70) return { label: 'Overbought', color: 'error' };
  if (v < 30) return { label: 'Oversold', color: 'success' };
  return { label: 'Neutral', color: 'default' };
};

const adxLabel = (v) => {
  if (v == null) return '—';
  if (v >= 25) return 'Strong';
  if (v >= 20) return 'Moderate';
  return 'Weak';
};

const trendChipColor = (dir) => {
  if (!dir) return 'default';
  const d = String(dir).toLowerCase();
  if (d.includes('up')) return 'success';
  if (d.includes('down')) return 'error';
  return 'default';
};

const PAModal_Momentum = ({ tpData, data, source = 'portfolio', pdfMode = false, userId, onCreateJiraTicket }) => {
  const [tab, setTab] = useState(0);
  const [sortCol, setSortCol] = useState('one_month_performance');
  const [sortDir, setSortDir] = useState('desc');

  const rows = useMemo(() => {
    return Object.entries(tpData).map(([ticker, tp]) => {
      const position = data.find(d => (d.Ticker || '').toUpperCase() === ticker.toUpperCase());
      return {
        ticker,
        company: position?.Company || '',
        oneDay: pf(tp.one_day_perf),
        oneWeek: pf(tp.one_week_performance),
        oneMonth: pf(tp.one_month_performance),
        sixMonth: pf(tp.six_month_performance),
        ytd: pf(tp.YTD),
        trendDir: tp.price_trend_direction || '',
        trendStrength: tp.price_trend_strength || '',
        ma30Slope: pf(tp.ma30_slope, 4),
        priceVsMa30: pf(tp.price_vs_ma30),
        rsi: pf(tp.rsi_14),
        adx: pf(tp.adx_14),
        volSpike: pf(tp.volume_spike_ratio),
        secularAlignment: tp.secular_alignment || '',
        selectNowMomentum: pf(tp.select_now_momentum, 4),
        selectNowScore: pf(tp.select_now_score, 4),
        selectNowPosition: tp.select_now_position != null ? parseInt(tp.select_now_position, 10) : null,
        returnProb: pf(tp.return_probability),
        expectedQReturn: pf(tp.expected_q_return, 3),
        riskAdjQReturn: pf(tp.risk_adj_q_return, 3),
        residualVol: pf(tp.residual_volatility),
        regime: tp.return_probability_regime || '',
      };
    });
  }, [tpData, data]);

  // --- Section 4: Portfolio Momentum Summary ---
  const summary = useMemo(() => {
    const valid1M = rows.filter(r => r.oneMonth != null);
    const avg1M = valid1M.length ? valid1M.reduce((s, r) => s + r.oneMonth, 0) / valid1M.length : null;

    const trendingUp = rows.filter(r => String(r.trendDir).toLowerCase().includes('up')).length;
    const trendingDown = rows.filter(r => String(r.trendDir).toLowerCase().includes('down')).length;
    const totalWithTrend = rows.filter(r => r.trendDir).length;

    const validRsi = rows.filter(r => r.rsi != null);
    const avgRsi = validRsi.length ? validRsi.reduce((s, r) => s + r.rsi, 0) / validRsi.length : null;
    const overbought = validRsi.filter(r => r.rsi > 70).length;
    const oversold = validRsi.filter(r => r.rsi < 30).length;

    const validRetProb = rows.filter(r => r.returnProb != null);
    const avgRetProb = validRetProb.length
      ? validRetProb.reduce((s, r) => s + r.returnProb, 0) / validRetProb.length
      : null;

    const valid1D = rows.filter(r => r.oneDay != null);
    const avg1D = valid1D.length ? valid1D.reduce((s, r) => s + r.oneDay, 0) / valid1D.length : null;

    return { avg1M, avg1D, trendingUp, trendingDown, totalWithTrend, avgRsi, overbought, oversold, avgRetProb };
  }, [rows]);

  // Sorting
  const sortedRows = useMemo(() => {
    const fieldMap = {
      ticker: 'ticker',
      one_day_perf: 'oneDay',
      one_week_performance: 'oneWeek',
      one_month_performance: 'oneMonth',
      six_month_performance: 'sixMonth',
      YTD: 'ytd',
      trendDir: 'trendDir',
      trendStrength: 'trendStrength',
      ma30Slope: 'ma30Slope',
      priceVsMa30: 'priceVsMa30',
      rsi: 'rsi',
      adx: 'adx',
      volSpike: 'volSpike',
      selectNowMomentum: 'selectNowMomentum',
      selectNowScore: 'selectNowScore',
      selectNowPosition: 'selectNowPosition',
      returnProb: 'returnProb',
      expectedQReturn: 'expectedQReturn',
      riskAdjQReturn: 'riskAdjQReturn',
      residualVol: 'residualVol',
    };
    const key = fieldMap[sortCol] || 'oneMonth';
    return [...rows].sort((a, b) => {
      const av = a[key], bv = b[key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });
  }, [rows, sortCol, sortDir]);

  const handleSort = (col) => {
    if (sortCol === col) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('desc');
    }
  };

  if (rows.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ minHeight: 200 }}>
        <Typography color="text.secondary">No momentum data available</Typography>
      </Box>
    );
  }

  const SortableHeader = ({ id, children, align = 'right' }) => (
    <TableCell align={align} sx={{ fontWeight: 600 }}>
      <TableSortLabel
        active={sortCol === id}
        direction={sortCol === id ? sortDir : 'desc'}
        onClick={() => handleSort(id)}
      >
        {children}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box>
      {/* === Section 4: Portfolio Momentum Summary === */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
        {summary.avg1D != null && (
          <Tooltip title="Average 1-day performance across all tickers">
            <Chip
              icon={summary.avg1D >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
              label={`Avg 1D: ${signed(summary.avg1D)}`}
              size="small"
              sx={{ fontWeight: 600, color: perfColor(summary.avg1D) }}
              variant="outlined"
            />
          </Tooltip>
        )}
        {summary.avg1M != null && (
          <Tooltip title="Average 1-month performance across all tickers">
            <Chip
              icon={summary.avg1M >= 0 ? <TrendingUpIcon /> : <TrendingDownIcon />}
              label={`Avg 1M: ${signed(summary.avg1M)}`}
              size="small"
              sx={{ fontWeight: 600, color: perfColor(summary.avg1M) }}
              variant="outlined"
            />
          </Tooltip>
        )}
        {summary.totalWithTrend > 0 && (
          <Tooltip title={`${summary.trendingUp} trending up, ${summary.trendingDown} trending down`}>
            <Chip
              label={`${summary.trendingUp}/${summary.totalWithTrend} trending up`}
              size="small"
              color={summary.trendingUp > summary.trendingDown ? 'success' : 'error'}
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Tooltip>
        )}
        {summary.avgRsi != null && (
          <Tooltip title={`Average RSI across ${rows.filter(r => r.rsi != null).length} tickers`}>
            <Chip
              label={`Avg RSI: ${fmt(summary.avgRsi)}`}
              size="small"
              color={rsiZone(summary.avgRsi).color}
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Tooltip>
        )}
        {summary.overbought > 0 && (
          <Tooltip title={`${summary.overbought} ticker(s) with RSI > 70`}>
            <Chip
              icon={<WarningAmberIcon />}
              label={`${summary.overbought} overbought`}
              size="small"
              color="error"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Tooltip>
        )}
        {summary.oversold > 0 && (
          <Tooltip title={`${summary.oversold} ticker(s) with RSI < 30`}>
            <Chip
              icon={<WarningAmberIcon />}
              label={`${summary.oversold} oversold`}
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Tooltip>
        )}
        {summary.avgRetProb != null && (
          <Tooltip title="Average return probability across all tickers">
            <Chip
              label={`Avg Return Prob: ${fmt(summary.avgRetProb)}%`}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 600 }}
            />
          </Tooltip>
        )}
        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
          {rows.length} tickers
        </Typography>
      </Stack>

      {/* === Tab navigation for Sections 1-3 === */}
      {!pdfMode && (
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Performance" />
          <Tab label="Technical" />
          <Tab label="Quantitative" />
        </Tabs>
      )}

      {/* === Section 1: Multi-Timeframe Performance Heatmap === */}
      {(pdfMode || tab === 0) && (
        <Box data-pdf-section="Performance" sx={pdfMode ? { mb: 3 } : undefined}>
          {pdfMode && <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Performance</Typography>}
          {userId === 2 && onCreateJiraTicket && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
              <Tooltip title="Create Jira Ticket – Performance">
                <IconButton size="small" onClick={() => onCreateJiraTicket('Momentum – Performance')}>
                  <ConfirmationNumberIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: pdfMode ? undefined : '55vh' }}>
          <MuiTable size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <SortableHeader id="ticker" align="left">Ticker</SortableHeader>
                <SortableHeader id="one_day_perf">1D</SortableHeader>
                <SortableHeader id="one_week_performance">1W</SortableHeader>
                <SortableHeader id="one_month_performance">1M</SortableHeader>
                <SortableHeader id="six_month_performance">6M</SortableHeader>
                <SortableHeader id="YTD">YTD</SortableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((row) => (
                <TableRow key={row.ticker} hover>
                  <TableCell>
                    <Box>
                      <ClickableTickerChip ticker={row.ticker} source={source} />
                      {row.company && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                          {row.company}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  {[row.oneDay, row.oneWeek, row.oneMonth, row.sixMonth, row.ytd].map((v, i) => (
                    <TableCell
                      key={i}
                      align="right"
                      sx={{ color: perfColor(v), bgcolor: perfBgColor(v), fontWeight: 600, fontSize: '0.8rem' }}
                    >
                      {signed(v)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </MuiTable>
        </TableContainer>
        </Box>
      )}

      {/* === Section 2: Trend & Technical Signals === */}
      {(pdfMode || tab === 1) && (
        <Box data-pdf-section="Technical" sx={pdfMode ? { mb: 3 } : undefined}>
          {pdfMode && <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Technical</Typography>}
          {userId === 2 && onCreateJiraTicket && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
              <Tooltip title="Create Jira Ticket – Technical">
                <IconButton size="small" onClick={() => onCreateJiraTicket('Momentum – Technical')}>
                  <ConfirmationNumberIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: pdfMode ? undefined : '55vh' }}>
          <MuiTable size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <SortableHeader id="ticker" align="left">Ticker</SortableHeader>
                <SortableHeader id="trendDir" align="left">Trend</SortableHeader>
                <SortableHeader id="trendStrength" align="left">Strength</SortableHeader>
                <SortableHeader id="ma30Slope">MA30 Slope</SortableHeader>
                <SortableHeader id="priceVsMa30">Price vs MA30</SortableHeader>
                <SortableHeader id="rsi">RSI</SortableHeader>
                <SortableHeader id="adx">ADX</SortableHeader>
                <SortableHeader id="volSpike">Vol Spike</SortableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((row) => {
                const rsiInfo = rsiZone(row.rsi);
                return (
                  <TableRow key={row.ticker} hover>
                    <TableCell>
                      <Box>
                        <ClickableTickerChip ticker={row.ticker} source={source} />
                        {row.company && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                            {row.company}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {row.trendDir && (
                        <Chip label={row.trendDir} size="small" color={trendChipColor(row.trendDir)} sx={{ height: 22, fontSize: '0.7rem' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {row.trendStrength && (
                        <Chip label={row.trendStrength} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ color: row.ma30Slope != null ? (row.ma30Slope >= 0 ? '#4caf50' : '#e53935') : undefined }}>
                      {row.ma30Slope != null ? row.ma30Slope.toFixed(4) : '—'}
                    </TableCell>
                    <TableCell align="right" sx={{ color: perfColor(row.priceVsMa30) }}>
                      {row.priceVsMa30 != null ? `${row.priceVsMa30 >= 0 ? '+' : ''}${row.priceVsMa30.toFixed(1)}%` : '—'}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                          {fmt(row.rsi)}
                        </Typography>
                        {row.rsi != null && rsiInfo.label !== 'Neutral' && (
                          <Chip label={rsiInfo.label} size="small" color={rsiInfo.color} sx={{ height: 18, fontSize: '0.55rem' }} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={`ADX: ${adxLabel(row.adx)} trend`}>
                        <Typography variant="body2" sx={{ fontWeight: row.adx != null && row.adx >= 25 ? 700 : 400, fontSize: '0.8rem' }}>
                          {fmt(row.adx)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: row.volSpike != null && row.volSpike >= 2 ? 700 : 400, color: row.volSpike != null && row.volSpike >= 2 ? '#e65100' : undefined }}>
                      {row.volSpike != null ? `${row.volSpike.toFixed(1)}x` : '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </MuiTable>
        </TableContainer>
        </Box>
      )}

      {/* === Section 3: Quantitative Momentum Signals === */}
      {(pdfMode || tab === 2) && (
        <Box data-pdf-section="Quantitative" sx={pdfMode ? { mb: 3 } : undefined}>
          {pdfMode && <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>Quantitative</Typography>}
          {userId === 2 && onCreateJiraTicket && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
              <Tooltip title="Create Jira Ticket – Quantitative">
                <IconButton size="small" onClick={() => onCreateJiraTicket('Momentum – Quantitative')}>
                  <ConfirmationNumberIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: pdfMode ? undefined : '55vh' }}>
          <MuiTable size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <SortableHeader id="ticker" align="left">Ticker</SortableHeader>
                <SortableHeader id="selectNowMomentum">Momentum</SortableHeader>
                <SortableHeader id="selectNowScore">Select Now</SortableHeader>
                <SortableHeader id="selectNowPosition">Rank</SortableHeader>
                <SortableHeader id="returnProb">Return Prob</SortableHeader>
                <SortableHeader id="expectedQReturn">Exp. Q Return</SortableHeader>
                <SortableHeader id="riskAdjQReturn">Risk-Adj Q</SortableHeader>
                <SortableHeader id="residualVol">Resid. Vol</SortableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((row) => (
                <TableRow key={row.ticker} hover>
                  <TableCell>
                    <Box>
                      <ClickableTickerChip ticker={row.ticker} source={source} />
                      {row.company && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                          {row.company}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {row.selectNowMomentum != null ? row.selectNowMomentum.toFixed(4) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {row.selectNowScore != null ? row.selectNowScore.toFixed(4) : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {row.selectNowPosition != null ? `#${row.selectNowPosition}` : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: row.returnProb != null ? (row.returnProb >= 50 ? '#4caf50' : '#e53935') : undefined }}>
                    {row.returnProb != null ? `${row.returnProb.toFixed(1)}%` : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: row.expectedQReturn != null ? (row.expectedQReturn >= 0 ? '#4caf50' : '#e53935') : undefined }}>
                    {row.expectedQReturn != null ? `${row.expectedQReturn >= 0 ? '+' : ''}${row.expectedQReturn.toFixed(3)}` : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: row.riskAdjQReturn != null ? (row.riskAdjQReturn >= 0 ? '#2e7d32' : '#c62828') : undefined }}>
                    {row.riskAdjQReturn != null ? `${row.riskAdjQReturn >= 0 ? '+' : ''}${row.riskAdjQReturn.toFixed(3)}` : '—'}
                  </TableCell>
                  <TableCell align="right">
                    {row.residualVol != null ? `${row.residualVol.toFixed(1)}%` : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </MuiTable>
        </TableContainer>

        {/* Regime legend */}
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap' }}>
          {[...new Set(rows.map(r => r.regime).filter(Boolean))].sort().map(r => (
            <Chip key={r} label={`Regime: ${r}`} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
          ))}
        </Stack>
        </Box>
      )}
    </Box>
  );
};

export default PAModal_Momentum;
